import { useT } from "@/shared/i18n/i18n";
import type {
  Cell,
  ColumnDef,
  ColumnOrderState,
  ColumnSizingState,
  Column,
  Header,
  Row,
  RowSelectionState,
  SortingState,
  Table as TanstackTable,
  Updater,
  VisibilityState,
} from "@tanstack/react-table";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import type { DragEndEvent } from "@dnd-kit/core";
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  DndSortableList,
  MultiContainerDnd,
  useDndContainer,
  useDndSortableItem,
  type MultiContainerDropResult,
} from "@/shared/ui/multi-container-dnd";
import { ChevronDown, ChevronsUpDown, GripVertical, Settings2 } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ContextMenu, ContextMenuContent, ContextMenuTrigger } from "@/components/ui/context-menu";

type DataTableColumnMeta = {
  cellClassName?: string;
  headerClassName?: string;
  locked?: boolean;
};

const dataTableRowDndModifiers = [restrictToVerticalAxis];

type DataTableRowSize = "compact" | "large" | "medium";

type DataTableGroup<TData> = {
  key: string;
  label: ReactNode;
  rows: Row<TData>[];
};

type DataTableRowReorderInput<TData> = {
  activeRow: Row<TData>;
  nextRow: Row<TData> | null;
  overRow: Row<TData> | null;
  previousRow: Row<TData> | null;
  sourceGroup: DataTableGroup<TData>;
  targetGroup: DataTableGroup<TData>;
  targetIndex: number;
};

type DataTableProps<TData, TValue> = {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  className?: string;
  tableClassName?: string;
  defaultColumnOrder?: string[];
  emptyState?: ReactNode;
  enableColumnReordering?: boolean;
  enableColumnResizing?: boolean;
  enableColumnVisibility?: boolean;
  enableRowReordering?: boolean;
  enableRowSelection?: boolean;
  columnOptionsHostColumnId?: string;
  fillColumnId?: string;
  fixedColumnIds?: string[];
  stickyColumnIds?: string[];
  getRowId?: (row: TData, index: number, parent?: Row<TData>) => string;
  globalFilter?: string;
  groupRows?: (rows: Row<TData>[], table: TanstackTable<TData>) => DataTableGroup<TData>[];
  onColumnOrderChange?: (columnOrder: string[]) => void;
  onGlobalFilterChange?: (value: string) => void;
  onRowClick?: (row: Row<TData>, table: TanstackTable<TData>) => void;
  onRowReorder?: (input: DataTableRowReorderInput<TData>) => void | Promise<void>;
  renderGroupAddRow?: (group: DataTableGroup<TData>, table: TanstackTable<TData>, state: { isDropTarget: boolean }) => ReactNode;
  renderColumnFooter?: (column: Column<TData, unknown>, table: TanstackTable<TData>, group: DataTableGroup<TData>) => ReactNode;
  renderTableFooter?: (column: Column<TData, unknown>, table: TanstackTable<TData>) => ReactNode;
  renderGroupHeader?: (group: DataTableGroup<TData>, table: TanstackTable<TData>) => ReactNode;
  renderRowContextMenu?: (row: Row<TData>, table: TanstackTable<TData>) => ReactNode;
  rowClassName?: (row: Row<TData>) => string | undefined;
  rowSize?: DataTableRowSize;
  toolbar?: (table: TanstackTable<TData>) => ReactNode;
  toolbarPlacement?: "floating" | "top";
  wrapText?: boolean;
};

export function DataTable<TData, TValue>({
  columns,
  data,
  className,
  tableClassName,
  defaultColumnOrder,
  emptyState,
  enableColumnReordering = true,
  enableColumnResizing = true,
  enableColumnVisibility = true,
  enableRowReordering = false,
  enableRowSelection = false,
  columnOptionsHostColumnId: columnOptionsHostColumnIdProp,
  fillColumnId: fillColumnIdProp,
  fixedColumnIds = [],
  stickyColumnIds,
  getRowId,
  globalFilter,
  groupRows,
  onColumnOrderChange,
  onGlobalFilterChange,
  onRowClick,
  onRowReorder,
  renderGroupAddRow,
  renderColumnFooter,
  renderTableFooter,
  renderGroupHeader,
  renderRowContextMenu,
  rowClassName,
  rowSize = "medium",
  toolbar,
  toolbarPlacement = "top",
  wrapText = false,
}: DataTableProps<TData, TValue>) {
  const baseColumnOrder = defaultColumnOrder ?? columns.map((column) => column.id).filter(Boolean) as string[];
  const initialColumnOrder = baseColumnOrder;
  const [columnOrder, setColumnOrder] = useStateFromDefault(initialColumnOrder);
  const [columnSizing, setColumnSizing] = useStateFromDefault<ColumnSizingState>({});
  const [columnVisibility, setColumnVisibility] = useStateFromDefault<VisibilityState>({});
  const [rowSelection, setRowSelection] = useStateFromDefault<RowSelectionState>({});
  const [sorting, setSorting] = useStateFromDefault<SortingState>([]);

  const table = useReactTable({
    columns,
    columnResizeMode: "onChange",
    data,
    enableColumnResizing,
    enableHiding: enableColumnVisibility,
    enableRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getRowId,
    getSortedRowModel: getSortedRowModel(),
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    onColumnVisibilityChange: enableColumnVisibility ? setColumnVisibility : undefined,
    onGlobalFilterChange: onGlobalFilterChange ? (updater) => onGlobalFilterChange(resolveUpdater(updater, globalFilter ?? "")) : undefined,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    state: {
      columnOrder,
      columnSizing,
      columnVisibility,
      globalFilter,
      rowSelection,
      sorting,
    },
  });
  const lockedColumnIds = new Set(fixedColumnIds);
  for (const column of table.getAllLeafColumns()) {
    if ((column.columnDef.meta as DataTableColumnMeta | undefined)?.locked) {
      lockedColumnIds.add(column.id);
    }
  }
  const isColumnReorderable = (columnId: string) => enableColumnReordering && !lockedColumnIds.has(columnId);
  const visibleLeafColumns = table.getVisibleLeafColumns();
  const stickyColumnIdSet = new Set(stickyColumnIds ?? fixedColumnIds);
  const firstStickyColumnId = visibleLeafColumns.find((column) => stickyColumnIdSet.has(column.id))?.id;
  const lastStickyColumnId = [...visibleLeafColumns].reverse().find((column) => stickyColumnIdSet.has(column.id))?.id;
  const visibleRows = table.getRowModel().rows;
  const groups = groupRows ? groupRows(visibleRows, table) : [{ key: "all", label: null, rows: visibleRows }];
  const toolbarContent = toolbar?.(table);
  const canReorderRows = enableRowReordering && sorting.length === 0;
  const hasRowControls = enableRowSelection || canReorderRows;
  const hasRowSelection = table.getIsSomeRowsSelected() || table.getIsAllRowsSelected();
  const hasColumnOptions = enableColumnVisibility && table.getAllLeafColumns().some((column) => column.getCanHide());
  const columnOptionsHostColumnId =
    hasColumnOptions && columnOptionsHostColumnIdProp
      ? visibleLeafColumns.find((column) => column.id === columnOptionsHostColumnIdProp && !column.getCanHide())?.id
      : undefined;
  const fillColumnId = fillColumnIdProp && visibleLeafColumns.some((column) => column.id === fillColumnIdProp) ? fillColumnIdProp : undefined;

  function fixedSide(columnId: string) {
    if (columnId === firstStickyColumnId) {
      return "left" as const;
    }
    if (columnId === lastStickyColumnId && lastStickyColumnId !== firstStickyColumnId) {
      return "right" as const;
    }
    return undefined;
  }

  function handleColumnOrderChange(nextColumnOrder: string[]) {
    setColumnOrder(nextColumnOrder);
    onColumnOrderChange?.(nextColumnOrder);
  }

  return (
    <DataTableGrid
      className={className}
      emptyState={emptyState}
      enableColumnResizing={enableColumnResizing}
      enableRowReordering={canReorderRows}
      enableRowSelection={enableRowSelection}
      fixedSide={fixedSide}
      fillColumnId={fillColumnId}
      groups={groups}
      hasRowControls={hasRowControls}
      hasRowSelection={hasRowSelection}
      isColumnReorderable={isColumnReorderable}
      columnOptionsHostColumnId={columnOptionsHostColumnId}
      onColumnOrderChange={handleColumnOrderChange}
      onRowClick={onRowClick}
      onRowReorder={onRowReorder}
      renderGroupAddRow={renderGroupAddRow}
      renderColumnFooter={renderColumnFooter}
      renderTableFooter={renderTableFooter}
      renderGroupHeader={renderGroupHeader}
      renderRowContextMenu={renderRowContextMenu}
      rowClassName={rowClassName}
      rowSize={rowSize}
      hasColumnOptions={hasColumnOptions}
      table={table}
      tableClassName={tableClassName}
      toolbarContent={toolbarContent}
      toolbarPlacement={toolbarPlacement}
      wrapText={wrapText}
    />
  );
}

function DataTableGrid<TData>({
  className,
  emptyState,
  enableColumnResizing,
  enableRowReordering,
  enableRowSelection,
  fixedSide,
  fillColumnId,
  groups,
  hasRowControls,
  hasRowSelection,
  isColumnReorderable,
  columnOptionsHostColumnId,
  hasColumnOptions,
  onColumnOrderChange,
  onRowClick,
  onRowReorder,
  renderGroupAddRow,
  renderColumnFooter,
  renderTableFooter,
  renderGroupHeader,
  renderRowContextMenu,
  rowClassName,
  rowSize,
  table,
  tableClassName,
  toolbarContent,
  toolbarPlacement,
  wrapText,
}: {
  className?: string;
  emptyState?: ReactNode;
  enableColumnResizing: boolean;
  enableRowReordering: boolean;
  enableRowSelection: boolean;
  fixedSide: (columnId: string) => "left" | "right" | undefined;
  fillColumnId?: string;
  groups: DataTableGroup<TData>[];
  hasRowControls: boolean;
  hasRowSelection: boolean;
  isColumnReorderable: (columnId: string) => boolean;
  columnOptionsHostColumnId?: string;
  hasColumnOptions: boolean;
  onColumnOrderChange: (columnOrder: string[]) => void;
  onRowClick?: (row: Row<TData>, table: TanstackTable<TData>) => void;
  onRowReorder?: (input: DataTableRowReorderInput<TData>) => void | Promise<void>;
  renderGroupAddRow?: (group: DataTableGroup<TData>, table: TanstackTable<TData>, state: { isDropTarget: boolean }) => ReactNode;
  renderColumnFooter?: (column: Column<TData, unknown>, table: TanstackTable<TData>, group: DataTableGroup<TData>) => ReactNode;
  renderTableFooter?: (column: Column<TData, unknown>, table: TanstackTable<TData>) => ReactNode;
  renderGroupHeader?: (group: DataTableGroup<TData>, table: TanstackTable<TData>) => ReactNode;
  renderRowContextMenu?: (row: Row<TData>, table: TanstackTable<TData>) => ReactNode;
  rowClassName?: (row: Row<TData>) => string | undefined;
  rowSize: DataTableRowSize;
  table: TanstackTable<TData>;
  tableClassName?: string;
  toolbarContent?: ReactNode;
  toolbarPlacement: "floating" | "top";
  wrapText: boolean;
}) {
  const visibleLeafColumns = table.getVisibleLeafColumns();
  const hasStandaloneColumnOptions = hasColumnOptions && !columnOptionsHostColumnId;
  const gridTemplateColumns = [
    ...visibleLeafColumns.map((column) =>
      column.id === fillColumnId ? `minmax(${column.getSize()}px, 1fr)` : `${column.getSize()}px`,
    ),
    ...(hasStandaloneColumnOptions ? ["32px"] : []),
  ].join(" ");
  const [projectedGroups, setProjectedGroups] = useState<DataTableGroup<TData>[] | null>(null);
  const [projectedSourceSignature, setProjectedSourceSignature] = useState<string | null>(null);
  const renderedGroups = projectedGroups ?? groups;
  const groupsSignature = dataTableGroupsSignature(groups);
  const projectedGroupsSignature = projectedGroups ? dataTableGroupsSignature(projectedGroups) : null;
  const hasGroupedSections = renderedGroups.some((group) => group.label != null);

  useEffect(() => {
    if (!projectedGroupsSignature) {
      return;
    }
    if (projectedGroupsSignature === groupsSignature || (projectedSourceSignature && projectedSourceSignature !== groupsSignature)) {
      setProjectedGroups(null);
      setProjectedSourceSignature(null);
    }
  }, [groupsSignature, projectedGroupsSignature, projectedSourceSignature]);

  function handleRowDragEnd(result: MultiContainerDropResult | null) {
    if (!result) {
      setProjectedGroups(null);
      setProjectedSourceSignature(null);
      return;
    }

    const sourceGroups = projectedGroups ?? groups;
    const sourceSignature = dataTableGroupsSignature(sourceGroups);
    const rowById = new Map(sourceGroups.flatMap((group) => group.rows.map((row) => [row.id, row] as const)));
    const nextGroups = sourceGroups.map((group) => {
      const container = result.containers.find((candidate) => candidate.key === group.key);
      if (!container) {
        return group;
      }
      return {
        ...group,
        rows: container.itemIds.map((id) => rowById.get(id)).filter((row): row is Row<TData> => Boolean(row)),
      };
    });
    const activeRow = groups.flatMap((group) => group.rows).find((row) => row.id === result.itemId);
    const sourceGroup = groups.find((group) => group.key === result.sourceContainerKey);
    const targetGroup = nextGroups.find((group) => group.key === result.targetContainerKey);

    if (!activeRow || !sourceGroup || !targetGroup) {
      setProjectedGroups(null);
      setProjectedSourceSignature(null);
      return;
    }

    const targetIndex = targetGroup.rows.findIndex((row) => row.id === result.itemId);
    const previousRow = targetIndex >= 0 ? targetGroup.rows[targetIndex - 1] ?? null : null;
    const nextRow = targetIndex >= 0 ? targetGroup.rows[targetIndex + 1] ?? null : null;
    setProjectedGroups(nextGroups);
    setProjectedSourceSignature(sourceSignature);
    void onRowReorder?.({
      activeRow,
      nextRow,
      overRow: nextRow,
      previousRow,
      sourceGroup,
      targetGroup,
      targetIndex,
    });
  }

  const renderGroupedRows = (liveGroups: DataTableGroup<TData>[]) => (
    <>
      {liveGroups.map((group) => (
        <DataTableGridGroupRows
          enableRowReordering={enableRowReordering}
          enableRowSelection={enableRowSelection}
          gridTemplateColumns={gridTemplateColumns}
          group={group}
          fixedSide={fixedSide}
          hasStandaloneColumnOptions={hasStandaloneColumnOptions}
          hasRowControls={hasRowControls}
          hasRowSelection={hasRowSelection}
          enableColumnResizing={enableColumnResizing}
          key={group.key}
          columnOptionsHostColumnId={columnOptionsHostColumnId}
          onRowClick={onRowClick}
          renderGroupAddRow={renderGroupAddRow}
          renderColumnFooter={renderColumnFooter}
          renderSectionHeaders={hasGroupedSections}
          renderGroupHeader={renderGroupHeader}
          renderRowContextMenu={renderRowContextMenu}
          rowClassName={rowClassName}
          rowSize={rowSize}
          hasColumnOptions={hasColumnOptions}
          table={table}
          visibleLeafColumns={visibleLeafColumns}
          wrapText={wrapText}
        />
      ))}
    </>
  );

  const rowByIdForDnd = enableRowReordering
    ? new Map(renderedGroups.flatMap((group) => group.rows.map((row) => [row.id, row] as const)))
    : null;

  return (
    <div className={cn("group/table relative flex min-h-0 w-full min-w-0 max-w-full flex-col", className)}>
      {toolbarContent && toolbarPlacement === "top" ? (
        <div className="flex items-center gap-2 px-4 py-2">
          <div className="min-w-0 flex-1">{toolbarContent}</div>
        </div>
      ) : null}
      <DataTableColumnDnd table={table} isColumnReorderable={isColumnReorderable} onColumnOrderChange={onColumnOrderChange}>
        <div className="relative w-full min-w-0 max-w-full overflow-x-auto">
          <div className={cn("min-w-full w-max", tableClassName)}>
            {!hasGroupedSections ? (
              <DataTableGridHeaderRows
                columnOptionsHostColumnId={columnOptionsHostColumnId}
                enableColumnResizing={enableColumnResizing}
                enableRowSelection={enableRowSelection}
                fixedSide={fixedSide}
                gridTemplateColumns={gridTemplateColumns}
                hasRowControls={hasRowControls}
                hasRowSelection={hasRowSelection}
                hasStandaloneColumnOptions={hasStandaloneColumnOptions}
                isColumnReorderable={isColumnReorderable}
                table={table}
                variant="sortable"
              />
            ) : null}
            {visibleLeafColumns.length > 0 && table.getRowModel().rows.length === 0 && emptyState ? (
              <div className={cn("relative grid min-h-20 items-center", hasRowControls && "px-12")} role="row" style={{ gridTemplateColumns }}>
                <div className="px-2 py-4" style={{ gridColumn: "1 / -1" }}>
                  {emptyState}
                </div>
              </div>
            ) : null}
            {enableRowReordering ? (
              <MultiContainerDnd
                containers={renderedGroups.map((group) => ({ itemIds: group.rows.map((row) => row.id), key: group.key }))}
                modifiers={dataTableRowDndModifiers}
                onDragEnd={handleRowDragEnd}
              >
                {(liveContainers) =>
                  renderGroupedRows(
                    liveContainers.flatMap((container) => {
                      const group = renderedGroups.find((candidate) => candidate.key === container.key);
                      if (!group) {
                        return [];
                      }
                      // Rows follow the drag state so cross-group transfers
                      // render live while the pointer moves.
                      return [{
                        ...group,
                        rows: container.itemIds
                          .map((id) => rowByIdForDnd?.get(id))
                          .filter((row): row is Row<TData> => Boolean(row)),
                      }];
                    }),
                  )
                }
              </MultiContainerDnd>
            ) : (
              renderGroupedRows(renderedGroups)
            )}
            {renderTableFooter && groups.length > 1 && table.getRowModel().rows.length > 0 ? (
              <div
                className={cn("grid h-9 items-center border-t-2 border-border/70 text-xs", hasRowControls && "px-12")}
                role="row"
                style={{ gridTemplateColumns }}
              >
                {visibleLeafColumns.map((column) => (
                  <DataTableGridFooterCell column={column} fixedSide={fixedSide(column.id)} key={column.id}>
                    {renderTableFooter(column, table)}
                  </DataTableGridFooterCell>
                ))}
                {hasStandaloneColumnOptions ? <div aria-hidden="true" className="h-full" role="gridcell" /> : null}
              </div>
            ) : null}
          </div>
        </div>
      </DataTableColumnDnd>
      {toolbarContent && toolbarPlacement === "floating" ? (
        <div className="pointer-events-none fixed right-0 bottom-5 left-0 z-50 flex justify-center px-4">
          <div className="pointer-events-auto flex items-center gap-2 rounded-lg border border-border/70 bg-popover/95 px-2.5 py-2 shadow-xl backdrop-blur">
            {toolbarContent}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function DataTableGridHeaderRows<TData>({
  columnOptionsHostColumnId,
  enableColumnResizing,
  enableRowSelection,
  fixedSide,
  gridTemplateColumns,
  hasRowControls,
  hasRowSelection,
  hasStandaloneColumnOptions,
  isColumnReorderable,
  table,
  variant,
}: {
  columnOptionsHostColumnId?: string;
  enableColumnResizing: boolean;
  enableRowSelection: boolean;
  fixedSide: (columnId: string) => "left" | "right" | undefined;
  gridTemplateColumns: string;
  hasRowControls: boolean;
  hasRowSelection: boolean;
  hasStandaloneColumnOptions: boolean;
  isColumnReorderable: (columnId: string) => boolean;
  table: TanstackTable<TData>;
  variant: "sortable" | "static";
}) {
  return (
    <>
      {table.getHeaderGroups().map((headerGroup) => (
        <div
          className={cn("relative grid h-8 items-center border-b border-border/60 bg-transparent", hasRowControls && "px-12")}
          key={headerGroup.id}
          role="row"
          style={{ gridTemplateColumns }}
        >
          {hasRowControls ? (
            <DataTableGridHeaderControlCell
              enableRowSelection={enableRowSelection}
              hasRowSelection={hasRowSelection}
              table={table}
            />
          ) : null}
          {headerGroup.headers.map((header) =>
            variant === "sortable" ? (
              <DataTableGridHeaderCell
                fixedSide={fixedSide(header.column.id)}
                header={header}
                isReorderable={isColumnReorderable(header.column.id)}
                isResizable={enableColumnResizing && header.column.getCanResize()}
                key={header.id}
                columnOptionsSlot={header.column.id === columnOptionsHostColumnId ? <DataTableViewOptions table={table} /> : null}
              />
            ) : (
              <DataTableGridStaticHeaderCell
                fixedSide={fixedSide(header.column.id)}
                header={header}
                isResizable={enableColumnResizing && header.column.getCanResize()}
                key={header.id}
                columnOptionsSlot={header.column.id === columnOptionsHostColumnId ? <DataTableViewOptions table={table} /> : null}
              />
            ),
          )}
          {hasStandaloneColumnOptions ? <DataTableGridColumnOptionsCell table={table} /> : null}
        </div>
      ))}
    </>
  );
}

function DataTableGridFooterCell<TData>({
  children,
  column,
  fixedSide,
}: {
  children: ReactNode;
  column: Column<TData, unknown>;
  fixedSide?: "left" | "right";
}) {
  const meta = column.columnDef.meta as DataTableColumnMeta | undefined;

  return (
    <div
      className={cn(
        "flex h-full min-w-0 items-center px-2 py-0 text-xs font-normal text-muted-foreground",
        fixedColumnClassName(fixedSide, "footer"),
        meta?.cellClassName,
      )}
      role="gridcell"
    >
      {children}
    </div>
  );
}

function DataTableGridHeaderControlCell<TData>({
  enableRowSelection,
  hasRowSelection,
  table,
}: {
  enableRowSelection: boolean;
  hasRowSelection: boolean;
  table: TanstackTable<TData>;
}) {
  const t = useT();
  return (
    <div className="absolute top-0 left-0 z-20 flex h-full w-12 items-center justify-end pr-1" role="columnheader">
      <div className={cn("transition-opacity duration-150", hasRowSelection ? "opacity-100" : "opacity-0 group-hover/table:opacity-100")}>
        {enableRowSelection ? (
          <Checkbox
            aria-label={t("dataTable.selectAllRows")}
            checked={table.getIsAllPageRowsSelected()}
            indeterminate={!table.getIsAllPageRowsSelected() && table.getIsSomePageRowsSelected()}
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(Boolean(value))}
          />
        ) : null}
      </div>
    </div>
  );
}

function DataTableGridHeaderCell<TData>({
  columnOptionsSlot,
  fixedSide,
  header,
  isReorderable,
  isResizable,
}: {
  columnOptionsSlot?: ReactNode;
  fixedSide?: "left" | "right";
  header: Header<TData, unknown>;
  isReorderable: boolean;
  isResizable: boolean;
}) {
  const t = useT();
  const sortable = useSortable({ disabled: !isReorderable, id: header.column.id });
  const canSort = header.column.getCanSort();
  const sorted = header.column.getIsSorted();
  const meta = header.column.columnDef.meta as DataTableColumnMeta | undefined;
  const headerContent = header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext());

  return (
    <div
      className={cn(
        "group/header relative flex h-8 min-w-0 items-center gap-1 px-2 text-xs font-medium text-muted-foreground/80",
        isReorderable && "cursor-grab touch-none select-none active:cursor-grabbing",
        fixedColumnClassName(fixedSide, "header"),
        meta?.headerClassName,
      )}
      ref={sortable.setNodeRef}
      style={sortableColumnStyle(sortable)}
      {...(isReorderable ? sortable.attributes : {})}
      {...(isReorderable ? sortable.listeners : {})}
    >
      {headerContent ? (
        <button
          className={cn(
            "flex min-w-0 items-center gap-1.5 rounded-md text-left transition-colors",
            canSort ? "cursor-inherit hover:text-foreground" : "cursor-default",
          )}
          disabled={!canSort}
          onClick={header.column.getToggleSortingHandler()}
          type="button"
        >
          <span className="min-w-0 truncate">{headerContent}</span>
          {canSort ? (
            sorted === "asc" ? (
              <ChevronDown className="size-3.5 shrink-0 rotate-180" />
            ) : sorted === "desc" ? (
              <ChevronDown className="size-3.5 shrink-0" />
            ) : (
              <ChevronsUpDown className="size-3.5 shrink-0 opacity-55" />
            )
          ) : null}
        </button>
      ) : null}
      {columnOptionsSlot ? <div className="ml-auto flex shrink-0 items-center">{columnOptionsSlot}</div> : null}
      {isResizable ? (
        <button
          aria-label={t("a11y.resizeColumn", { column: columnLabel(header.column) })}
          className={cn(
            "absolute top-1/2 right-0 z-10 h-[26px] w-2 -translate-y-1/2 cursor-col-resize touch-none select-none",
            header.column.getIsResizing() && "bg-primary/10",
          )}
          onClick={(event) => event.stopPropagation()}
          onDoubleClick={(event) => {
            event.stopPropagation();
            header.column.resetSize();
          }}
          onMouseDown={(event) => {
            event.stopPropagation();
            header.getResizeHandler()(event);
          }}
          onTouchStart={(event) => {
            event.stopPropagation();
            header.getResizeHandler()(event);
          }}
          type="button"
        >
          <span
            className={cn(
              "absolute top-1 right-0 bottom-1 w-px bg-border/80 opacity-0 transition-opacity group-hover/header:opacity-100",
              header.column.getIsResizing() && "bg-primary opacity-100",
            )}
          />
        </button>
      ) : null}
    </div>
  );
}

function DataTableGridStaticHeaderCell<TData>({
  columnOptionsSlot,
  fixedSide,
  header,
  isResizable,
}: {
  columnOptionsSlot?: ReactNode;
  fixedSide?: "left" | "right";
  header: Header<TData, unknown>;
  isResizable: boolean;
}) {
  const t = useT();
  const canSort = header.column.getCanSort();
  const sorted = header.column.getIsSorted();
  const meta = header.column.columnDef.meta as DataTableColumnMeta | undefined;
  const headerContent = header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext());

  return (
    <div
      className={cn(
        "group/header relative flex h-8 min-w-0 items-center gap-1 px-2 text-xs font-medium text-muted-foreground/80",
        fixedColumnClassName(fixedSide, "header"),
        meta?.headerClassName,
      )}
    >
      {headerContent ? (
        <button
          className={cn(
            "flex min-w-0 items-center gap-1.5 rounded-md text-left transition-colors",
            canSort ? "cursor-pointer hover:text-foreground" : "cursor-default",
          )}
          disabled={!canSort}
          onClick={header.column.getToggleSortingHandler()}
          type="button"
        >
          <span className="min-w-0 truncate">{headerContent}</span>
          {canSort ? (
            sorted === "asc" ? (
              <ChevronDown className="size-3.5 shrink-0 rotate-180" />
            ) : sorted === "desc" ? (
              <ChevronDown className="size-3.5 shrink-0" />
            ) : (
              <ChevronsUpDown className="size-3.5 shrink-0 opacity-55" />
            )
          ) : null}
        </button>
      ) : null}
      {columnOptionsSlot ? <div className="ml-auto flex shrink-0 items-center">{columnOptionsSlot}</div> : null}
      {isResizable ? (
        <button
          aria-label={t("a11y.resizeColumn", { column: columnLabel(header.column) })}
          className={cn(
            "absolute top-1/2 right-0 z-10 h-[26px] w-2 -translate-y-1/2 cursor-col-resize touch-none select-none",
            header.column.getIsResizing() && "bg-primary/10",
          )}
          onClick={(event) => event.stopPropagation()}
          onDoubleClick={(event) => {
            event.stopPropagation();
            header.column.resetSize();
          }}
          onMouseDown={(event) => {
            event.stopPropagation();
            header.getResizeHandler()(event);
          }}
          onTouchStart={(event) => {
            event.stopPropagation();
            header.getResizeHandler()(event);
          }}
          type="button"
        >
          <span
            className={cn(
              "absolute top-1 right-0 bottom-1 w-px bg-border/80 opacity-0 transition-opacity group-hover/header:opacity-100",
              header.column.getIsResizing() && "bg-primary opacity-100",
            )}
          />
        </button>
      ) : null}
    </div>
  );
}

function dataTableGroupsSignature<TData>(groups: DataTableGroup<TData>[]) {
  return groups.map((group) => `${group.key}:${group.rows.map((row) => row.id).join(",")}`).join("|");
}


function DataTableGridGroupRows<TData>({
  columnOptionsHostColumnId,
  enableColumnResizing,
  enableRowReordering,
  enableRowSelection,
  fixedSide,
  gridTemplateColumns,
  group,
  hasColumnOptions,
  hasStandaloneColumnOptions,
  hasRowControls,
  hasRowSelection,
  renderColumnFooter,
  renderGroupHeader,
  renderSectionHeaders,
  renderGroupAddRow,
  onRowClick,
  renderRowContextMenu,
  rowClassName,
  rowSize,
  table,
  visibleLeafColumns,
  wrapText,
}: {
  columnOptionsHostColumnId?: string;
  enableColumnResizing: boolean;
  enableRowReordering: boolean;
  enableRowSelection: boolean;
  fixedSide: (columnId: string) => "left" | "right" | undefined;
  gridTemplateColumns: string;
  group: DataTableGroup<TData>;
  hasColumnOptions: boolean;
  hasStandaloneColumnOptions: boolean;
  hasRowControls: boolean;
  hasRowSelection: boolean;
  renderColumnFooter?: (column: Column<TData, unknown>, table: TanstackTable<TData>, group: DataTableGroup<TData>) => ReactNode;
  renderGroupHeader?: (group: DataTableGroup<TData>, table: TanstackTable<TData>) => ReactNode;
  renderSectionHeaders: boolean;
  renderGroupAddRow?: (group: DataTableGroup<TData>, table: TanstackTable<TData>, state: { isDropTarget: boolean }) => ReactNode;
  onRowClick?: (row: Row<TData>, table: TanstackTable<TData>) => void;
  renderRowContextMenu?: (row: Row<TData>, table: TanstackTable<TData>) => ReactNode;
  rowClassName?: (row: Row<TData>) => string | undefined;
  rowSize: DataTableRowSize;
  table: TanstackTable<TData>;
  visibleLeafColumns: Column<TData, unknown>[];
  wrapText: boolean;
}) {
  const t = useT();
  const footerCells = renderColumnFooter ? visibleLeafColumns.map((column) => renderColumnFooter(column, table, group)) : [];
  const hasFooter = footerCells.some(Boolean);
  const firstEmptyFooterCellIndex = footerCells.findIndex((cell) => cell == null);
  const footerAddRowIndex = hasFooter && renderGroupAddRow ? (firstEmptyFooterCellIndex >= 0 ? firstEmptyFooterCellIndex : 0) : -1;
  // No-ops when row reordering is off (there is no DndContext above).
  const { isOver: isDropTargetGroup, setNodeRef: setDroppableRef } = useDndContainer(group.key, {
    childCount: group.rows.length,
  });

  function renderFooterRow(isDropTarget: boolean) {
    if (!hasFooter) {
      return null;
    }

    return (
      <div
        className={cn("relative grid h-9 items-center border-b border-border/40 text-xs", hasRowControls && "px-12")}
        role="row"
        style={{ gridTemplateColumns }}
      >
        {visibleLeafColumns.map((column, index) => (
          <DataTableGridFooterCell
            column={column}
            fixedSide={fixedSide(column.id)}
            key={column.id}
          >
            {index === footerAddRowIndex ? renderGroupAddRow?.(group, table, { isDropTarget }) : footerCells[index]}
          </DataTableGridFooterCell>
        ))}
        {hasStandaloneColumnOptions ? <div aria-hidden="true" className="h-full" role="gridcell" /> : null}
      </div>
    );
  }

  return (
    <div className={cn("relative min-h-8 rounded-md", group.label && "pb-2")}>
      {group.label ? (
        <div className="relative flex h-9 items-center gap-2 text-xs text-muted-foreground">
          <Button aria-label={t("dataTable.collapseGroup")} className="size-5 text-muted-foreground/80" size="icon-xs" type="button" variant="ghost">
            <ChevronDown />
          </Button>
          <div className="inline-flex h-6 max-w-64 items-center gap-1.5 rounded-md bg-violet-600 px-2 text-xs font-medium text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.12)]">
            <span className="size-3 rounded-full border-2 border-current opacity-90" />
            <span className="min-w-0 truncate">{renderGroupHeader ? renderGroupHeader(group, table) : group.label}</span>
          </div>
          <span className="px-1 text-xs font-medium tabular-nums text-muted-foreground">{group.rows.length}</span>
        </div>
      ) : null}
      {renderSectionHeaders ? (
        <DataTableGridHeaderRows
          columnOptionsHostColumnId={columnOptionsHostColumnId}
          enableColumnResizing={enableColumnResizing}
          enableRowSelection={enableRowSelection}
          fixedSide={fixedSide}
          gridTemplateColumns={gridTemplateColumns}
          hasRowControls={hasRowControls}
          hasRowSelection={hasRowSelection}
          hasStandaloneColumnOptions={hasStandaloneColumnOptions && hasColumnOptions}
          isColumnReorderable={() => false}
          table={table}
          variant="static"
        />
      ) : null}
      {enableRowReordering ? (
        <div
          className={cn("min-h-8 rounded-md transition-colors", isDropTargetGroup && group.rows.length === 0 && "bg-muted/20")}
          ref={setDroppableRef}
        >
          <DndSortableList itemIds={group.rows.map((row) => row.id)}>
            {group.rows.map((row) => (
              <DataTableGridBodyRow
                enableRowSelection={enableRowSelection}
                fixedSide={fixedSide}
                gridTemplateColumns={gridTemplateColumns}
                hasStandaloneColumnOptions={hasStandaloneColumnOptions}
                hasRowControls={hasRowControls}
                hasRowSelection={hasRowSelection}
                isReorderable={enableRowReordering}
                key={row.id}
                onRowClick={onRowClick}
                renderRowContextMenu={renderRowContextMenu}
                row={row}
                rowClassName={rowClassName}
                rowSize={rowSize}
                table={table}
                wrapText={wrapText}
              />
            ))}
          </DndSortableList>
          {group.rows.length === 0 && !renderGroupAddRow ? (
            <DataTableGridEmptyGroupRow
              gridTemplateColumns={gridTemplateColumns}
              hasRowControls={hasRowControls}
              isDropTarget={isDropTargetGroup}
            />
          ) : null}
          {renderGroupAddRow && !hasFooter ? (
            <DataTableGridAddGroupRow
              gridTemplateColumns={gridTemplateColumns}
              hasRowControls={hasRowControls}
            >
              {renderGroupAddRow(group, table, { isDropTarget: isDropTargetGroup })}
            </DataTableGridAddGroupRow>
          ) : null}
          {renderFooterRow(isDropTargetGroup)}
        </div>
      ) : (
        <>
          {group.rows.map((row) => (
            <DataTableGridBodyRow
              enableRowSelection={enableRowSelection}
              fixedSide={fixedSide}
              gridTemplateColumns={gridTemplateColumns}
              hasStandaloneColumnOptions={hasStandaloneColumnOptions}
              hasRowControls={hasRowControls}
              hasRowSelection={hasRowSelection}
              isReorderable={false}
              key={row.id}
              onRowClick={onRowClick}
              renderRowContextMenu={renderRowContextMenu}
              row={row}
              rowClassName={rowClassName}
              rowSize={rowSize}
              table={table}
              wrapText={wrapText}
            />
          ))}
          {group.rows.length === 0 && !renderGroupAddRow ? (
            <DataTableGridEmptyGroupRow
              gridTemplateColumns={gridTemplateColumns}
              hasRowControls={hasRowControls}
              isDropTarget={false}
            />
          ) : null}
          {renderGroupAddRow && !hasFooter ? (
            <DataTableGridAddGroupRow
              gridTemplateColumns={gridTemplateColumns}
              hasRowControls={hasRowControls}
            >
              {renderGroupAddRow(group, table, { isDropTarget: false })}
            </DataTableGridAddGroupRow>
          ) : null}
          {renderFooterRow(false)}
        </>
      )}
    </div>
  );
}

function DataTableGridAddGroupRow({
  children,
  gridTemplateColumns,
  hasRowControls,
}: {
  children: ReactNode;
  gridTemplateColumns: string;
  hasRowControls: boolean;
}) {
  return (
    <div
      className={cn("relative grid h-8 items-center border-b border-border/40 text-xs", hasRowControls && "px-12")}
      role="row"
      style={{ gridTemplateColumns }}
    >
      <div className="flex h-full min-w-0 items-center px-2" role="gridcell">
        {children}
      </div>
    </div>
  );
}

function DataTableGridEmptyGroupRow({
  gridTemplateColumns,
  hasRowControls,
  isDropTarget,
}: {
  gridTemplateColumns: string;
  hasRowControls: boolean;
  isDropTarget: boolean;
}) {
  const t = useT();
  return (
    <div
      className={cn(
        "relative grid h-8 items-center border-b border-border/40 text-xs text-muted-foreground/55 transition-colors duration-150",
        hasRowControls && "px-12",
        isDropTarget && "bg-muted/25 text-muted-foreground/80",
      )}
      role="row"
      style={{ gridTemplateColumns }}
    >
      <div className={cn("flex h-full min-w-0 items-center px-2", isDropTarget && "opacity-80")} role="gridcell">
        <span className="truncate">{isDropTarget ? t("dataTable.dropHere") : t("dataTable.emptyGroup")}</span>
      </div>
    </div>
  );
}

function DataTableGridBodyRow<TData>({
  enableRowSelection,
  fixedSide,
  gridTemplateColumns,
  hasStandaloneColumnOptions,
  hasRowControls,
  hasRowSelection,
  isReorderable,
  onRowClick,
  renderRowContextMenu,
  row,
  rowClassName,
  rowSize,
  table,
  wrapText,
}: {
  enableRowSelection: boolean;
  fixedSide: (columnId: string) => "left" | "right" | undefined;
  gridTemplateColumns: string;
  hasStandaloneColumnOptions: boolean;
  hasRowControls: boolean;
  hasRowSelection: boolean;
  isReorderable: boolean;
  onRowClick?: (row: Row<TData>, table: TanstackTable<TData>) => void;
  renderRowContextMenu?: (row: Row<TData>, table: TanstackTable<TData>) => ReactNode;
  row: Row<TData>;
  rowClassName?: (row: Row<TData>) => string | undefined;
  rowSize: DataTableRowSize;
  table: TanstackTable<TData>;
  wrapText: boolean;
}) {
  const rowSizeClass = wrapText ? dataTableRowMinHeightClass(rowSize) : dataTableRowHeightClass(rowSize);
  // No-ops when the row is not reorderable (no DndContext above).
  const sortable = useDndSortableItem(row.id, { disabled: !isReorderable });
  const content = (
    <div
      className={cn(
        "group/row relative grid items-center border-b border-border/60 transition-colors hover:bg-muted/25",
        rowSizeClass,
        wrapText && "items-stretch",
        hasRowControls && "px-12",
        onRowClick && "cursor-pointer",
        sortable.isDragSource && "relative z-10 opacity-75",
        rowClassName?.(row),
      )}
      data-state={row.getIsSelected() && "selected"}
      data-table-row-id={row.id}
      onClick={
        onRowClick
          ? (event) => {
              if (shouldIgnoreDataTableRowOpen(event.target) || sortable.wasRecentlyDragged()) {
                return;
              }
              onRowClick(row, table);
            }
          : undefined
      }
      onKeyDown={
        onRowClick
          ? (event) => {
              if (shouldIgnoreDataTableRowOpen(event.target)) {
                return;
              }
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onRowClick(row, table);
              }
            }
          : undefined
      }
      ref={sortable.setNodeRef}
      tabIndex={onRowClick ? 0 : undefined}
      role="row"
      style={{
        ...sortable.style,
        gridTemplateColumns,
      }}
    >
      {hasRowControls ? (
        <DataTableGridRowControls
          dragHandleProps={sortable.dragProps}
          enableRowSelection={enableRowSelection}
          hasRowSelection={hasRowSelection}
          isReorderable={isReorderable}
          row={row}
        />
      ) : null}
      {row.getVisibleCells().map((cell) => (
        <DataTableGridBodyCell cell={cell} fixedSide={fixedSide(cell.column.id)} key={cell.id} rowSize={rowSize} wrapText={wrapText} />
      ))}
      {hasStandaloneColumnOptions ? <div aria-hidden="true" className="h-full" role="gridcell" /> : null}
    </div>
  );
  const menu = renderRowContextMenu?.(row, table);

  if (!menu) {
    return content;
  }

  return (
    <ContextMenu>
      <ContextMenuTrigger render={content} />
      <ContextMenuContent>{menu}</ContextMenuContent>
    </ContextMenu>
  );
}

function shouldIgnoreDataTableRowOpen(target: EventTarget | null) {
  if (!(target instanceof Element)) {
    return false;
  }

  if (window.getSelection()?.toString()) {
    return true;
  }

  return Boolean(
    target.closest(
      [
        "a",
        "button",
        "input",
        "textarea",
        "select",
        "[contenteditable='true']",
        "[role='button']",
        "[role='checkbox']",
        "[role='combobox']",
        "[role='menuitem']",
        "[role='option']",
        "[data-row-interactive]",
      ].join(","),
    ),
  );
}

function DataTableGridRowControls<TData>({
  dragHandleProps,
  enableRowSelection,
  hasRowSelection,
  isReorderable,
  row,
}: {
  dragHandleProps?: Record<string, unknown>;
  enableRowSelection: boolean;
  hasRowSelection: boolean;
  isReorderable: boolean;
  row: Row<TData>;
}) {
  const t = useT();
  return (
    <div className="absolute top-0 left-0 z-20 flex h-full w-12 items-center justify-end gap-1 pr-1">
      <div
        className={cn(
          "flex items-center justify-end gap-1 transition-opacity duration-150",
          hasRowSelection || row.getIsSelected() ? "opacity-100" : "opacity-0 group-hover/table:opacity-100 group-hover/row:opacity-100",
        )}
      >
        {isReorderable ? (
          <Button
            aria-label={t("dataTable.dragRow")}
            className="size-4 cursor-grab touch-none rounded-[4px] p-0 text-muted-foreground/65 opacity-0 transition-opacity active:cursor-grabbing group-hover/row:opacity-100 [&_svg:not([class*='size-'])]:size-3.5"
            size="icon-xs"
            type="button"
            variant="ghost"
            {...dragHandleProps}
          >
            <GripVertical />
          </Button>
        ) : null}
        {enableRowSelection ? (
          <Checkbox
            aria-label={t("dataTable.selectRow")}
            checked={row.getIsSelected()}
            className={cn(
              "border-primary/70 bg-transparent transition-opacity after:inset-0",
              hasRowSelection || row.getIsSelected() ? "opacity-100" : "opacity-0 group-hover/table:opacity-100 group-hover/row:opacity-100",
            )}
            disabled={!row.getCanSelect()}
            onCheckedChange={(value) => row.toggleSelected(Boolean(value))}
          />
        ) : null}
      </div>
    </div>
  );
}

function DataTableGridBodyCell<TData>({
  cell,
  fixedSide,
  rowSize,
  wrapText,
}: {
  cell: Cell<TData, unknown>;
  fixedSide?: "left" | "right";
  rowSize: DataTableRowSize;
  wrapText: boolean;
}) {
  const meta = cell.column.columnDef.meta as DataTableColumnMeta | undefined;

  return (
    <div
      className={cn(
        "flex min-w-0 px-2 text-xs font-normal",
        wrapText
          ? cn(
              dataTableRowMinHeightClass(rowSize),
              "items-start py-1.5 [&_button]:h-auto [&_button]:min-h-6 [&_button]:overflow-visible [&_button]:text-clip [&_button]:whitespace-normal [&_span]:break-words [&_span]:overflow-visible [&_span]:text-clip [&_span]:whitespace-normal",
            )
          : "h-full items-center py-0",
        fixedColumnClassName(fixedSide, "cell"),
        meta?.cellClassName,
      )}
      role="gridcell"
    >
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </div>
  );
}

function dataTableRowHeightClass(rowSize: DataTableRowSize) {
  if (rowSize === "compact") return "h-7";
  if (rowSize === "large") return "h-10";
  return "h-8";
}

function dataTableRowMinHeightClass(rowSize: DataTableRowSize) {
  if (rowSize === "compact") return "min-h-7";
  if (rowSize === "large") return "min-h-10";
  return "min-h-8";
}

function DataTableGridColumnOptionsCell<TData>({ table }: { table: TanstackTable<TData> }) {
  return (
    <div className="flex h-full items-center justify-center" role="columnheader">
      <DataTableViewOptions table={table} />
    </div>
  );
}

function DataTableViewOptions<TData>({ table }: { table: TanstackTable<TData> }) {
  const t = useT();
  const columns = table.getAllLeafColumns().filter((column) => column.getCanHide());
  if (columns.length === 0) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button aria-label={t("dataTable.tableColumns")} className="size-6 text-muted-foreground/70" size="icon-xs" title={t("dataTable.columns")} variant="ghost">
            <Settings2 />
          </Button>
        }
      />
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuGroup>
          <DropdownMenuLabel>{t("dataTable.visibleColumns")}</DropdownMenuLabel>
          {columns.map((column) => (
            <DropdownMenuCheckboxItem
              checked={column.getIsVisible()}
              key={column.id}
              onCheckedChange={(value) => column.toggleVisibility(Boolean(value))}
            >
              {columnLabel(column)}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function DataTableColumnDnd<TData>({
  children,
  isColumnReorderable,
  onColumnOrderChange,
  table,
}: {
  children: ReactNode;
  isColumnReorderable: (columnId: string) => boolean;
  onColumnOrderChange: (columnOrder: string[]) => void;
  table: TanstackTable<TData>;
}) {
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const visibleColumnIds = table.getVisibleLeafColumns().map((column) => column.id);

  function moveColumn(event: DragEndEvent) {
    const activeColumnId = String(event.active.id);
    const overColumnId = event.over ? String(event.over.id) : "";
    if (!overColumnId || activeColumnId === overColumnId || !isColumnReorderable(activeColumnId)) {
      return;
    }

    const currentColumnOrder = table.getState().columnOrder;
    const oldIndex = currentColumnOrder.indexOf(activeColumnId);
    const overIndex = currentColumnOrder.indexOf(overColumnId);
    if (oldIndex < 0 || overIndex < 0) {
      return;
    }

    const reorderableIndexes = currentColumnOrder
      .map((columnId, index) => (isColumnReorderable(columnId) ? index : -1))
      .filter((index) => index >= 0);
    const firstReorderableIndex = reorderableIndexes[0];
    const lastReorderableIndex = reorderableIndexes.at(-1);
    if (firstReorderableIndex === undefined || lastReorderableIndex === undefined) {
      return;
    }

    const nextIndex = isColumnReorderable(overColumnId)
      ? overIndex
      : overIndex < oldIndex
        ? firstReorderableIndex
        : lastReorderableIndex;

    if (oldIndex === nextIndex) {
      return;
    }
    onColumnOrderChange(arrayMove(currentColumnOrder, oldIndex, nextIndex));
  }

  return (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={[restrictToHorizontalAxis]}
      onDragEnd={moveColumn}
      sensors={sensors}
    >
      <SortableContext items={visibleColumnIds} strategy={horizontalListSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
}

function sortableColumnStyle(sortable: ReturnType<typeof useSortable>): CSSProperties {
  return {
    opacity: sortable.isDragging ? 0.85 : undefined,
    transform: CSS.Translate.toString(sortable.transform),
    transition: sortable.transition,
    whiteSpace: "nowrap",
  };
}

function fixedColumnClassName(fixedSide: "left" | "right" | undefined, slot: "cell" | "footer" | "header") {
  if (!fixedSide) {
    return undefined;
  }

  return cn(
    "sticky min-w-0 overflow-hidden bg-card",
    slot === "header" ? "z-30 border-b border-border/60" : "z-20",
    slot === "cell" &&
      "before:pointer-events-none before:absolute before:inset-0 before:bg-muted/25 before:opacity-0 before:transition-opacity group-hover/row:before:opacity-100",
    fixedSide === "left" ? "left-0" : "right-0",
  );
}

function columnLabel<TData>(column: Column<TData, unknown>) {
  if (typeof column.columnDef.header === "string") {
    return column.columnDef.header;
  }
  return column.id;
}

function resolveUpdater<T>(updater: Updater<T>, previous: T) {
  return updater instanceof Function ? updater(previous) : updater;
}

function useStateFromDefault<T>(value: T) {
  return useState<T>(value);
}

export type { DataTableColumnMeta, DataTableGroup, DataTableRowSize };
